import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { QuestionData } from '../../data';
import {
    Box,
    Card,
    Checkbox,
    Divider,
    InputAdornment,
    Link,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
} from '@mui/material';
import SearchIcon from '../../icons/Search';
import LockIcon from '../../icons/Lock';
import Scrollbar from '../Scrollbar';
import Label from '../Label';

type Status = 'published' | 'draft' | 'archived';

const getStatusLabel = (status: Status): JSX.Element => {
    const map = {
        published: {
            color: 'success',
            text: 'Published',
        },
        draft: {
            color: 'warning',
            text: 'Draft',
        },
        archived: {
            color: 'error',
            text: 'Archived',
        },
    };

    const { text, color }: any = map[status];

    return <Label color={color}>{text}</Label>;
};

interface QuestionListTableProps {
    questions: QuestionData[];
}

const tabs = [
    {
        label: 'All',
        value: 'all',
    },
    {
        label: 'Published',
        value: 'published',
    },
    {
        label: 'Drafts',
        value: 'drafts',
    },
    {
        label: 'Archived',
        value: 'archived',
    },
];

const applyFilters = (questions: QuestionData[], query: string, filters: any): QuestionData[] => {
    query = query.toLowerCase();
    return questions.filter((question) => {
        let matches = true;

        if (query) {
            let containsQuery = false;
            if (question.title.toLowerCase().includes(query) || question.labels.toLowerCase().includes(query)) {
                containsQuery = true;
            }

            if (!containsQuery) {
                matches = false;
            }
        }

        if (filters['published']) {
            if (!question.isPublished || question.isArchived) {
                matches = false;
            }
        } else if (filters['drafts']) {
            if (question.isPublished || question.isArchived) {
                matches = false;
            }
        } else if (filters['archived']) {
            if (!question.isArchived) {
                matches = false;
            }
        }

        return matches;
    });
};

const applyPagination = (questions: QuestionData[], page: number, limit: number): QuestionData[] =>
    questions.slice(page * limit, page * limit + limit);

export const QuestionListTable = (props: QuestionListTableProps) => {
    const { questions, ...other } = props;
    const [currentTab, setCurrentTab] = useState<string>('all');
    const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
    const [page, setPage] = useState<number>(0);
    const [limit, setLimit] = useState<number>(5);
    const [query, setQuery] = useState<string>('');
    const [filters, setFilters] = useState<any>({
        published: null,
        drafts: null,
        archived: null,
    });

    const handleTabsChange = (event: ChangeEvent<{}>, value: string): void => {
        const updatedFilters = {
            published: null,
            drafts: null,
            archived: null,
        };

        if (value !== 'all') {
            updatedFilters[value] = true;
        }

        setFilters(updatedFilters);
        setSelectedQuestions([]);
        setCurrentTab(value);
        setPage(0);
    };

    const handleQueryChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setQuery(event.target.value);
        setPage(0);
    };

    const handleSelectAllQuestions = (event: ChangeEvent<HTMLInputElement>): void => {
        setSelectedQuestions(event.target.checked ? questions.map((question) => question.questionId) : []);
    };

    const handleSelectOneQuestion = (event: ChangeEvent<HTMLInputElement>, questionId: number): void => {
        if (!selectedQuestions.includes(questionId)) {
            setSelectedQuestions((prevSelected) => [...prevSelected, questionId]);
        } else {
            setSelectedQuestions((prevSelected) => prevSelected.filter((id) => id !== questionId));
        }
    };

    const handlePageChange = (event: any, newPage: number): void => {
        setPage(newPage);
    };

    const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredQuestions = applyFilters(questions, query, filters);
    const paginatedQuestions = applyPagination(filteredQuestions, page, limit);
    const selectedSomeQuestions = selectedQuestions.length > 0 && selectedQuestions.length < questions.length;
    const selectedAllQuestions = selectedQuestions.length === questions.length;

    return (
        <Card {...other}>
            <Tabs
                indicatorColor="primary"
                onChange={handleTabsChange}
                scrollButtons="auto"
                textColor="primary"
                value={currentTab}
                variant="scrollable"
            >
                {tabs.map((tab) => (
                    <Tab key={tab.value} label={tab.label} value={tab.value} />
                ))}
            </Tabs>
            <Divider />
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    m: -1,
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        m: 1,
                        maxWidth: '100%',
                        width: 500,
                    }}
                >
                    <TextField
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        onChange={handleQueryChange}
                        placeholder="Search articles"
                        value={query}
                        variant="outlined"
                    />
                </Box>
            </Box>
            <Scrollbar>
                <Box sx={{ minWidth: 700 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedAllQuestions}
                                        color="primary"
                                        indeterminate={selectedSomeQuestions}
                                        onChange={handleSelectAllQuestions}
                                    />
                                </TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Private</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedQuestions.map((question) => {
                                const isQuestionSelected = selectedQuestions.includes(question.questionId);

                                return (
                                    <TableRow hover key={question.questionId} selected={isQuestionSelected}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isQuestionSelected}
                                                color="primary"
                                                onChange={(event) =>
                                                    handleSelectOneQuestion(event, question.questionId)
                                                }
                                                value={isQuestionSelected}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Box>
                                                    <Link
                                                        color="inherit"
                                                        component={RouterLink}
                                                        to={`/kb/questions/${question.questionId}`}
                                                    >
                                                        {question.title}
                                                    </Link>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{`${question.categoryName ?? ''}`}</TableCell>
                                        <TableCell>
                                            {getStatusLabel(
                                                question.isArchived
                                                    ? 'archived'
                                                    : question.isPublished
                                                    ? 'published'
                                                    : 'draft'
                                            )}
                                        </TableCell>
                                        <TableCell>{question.isPublic ? '' : <LockIcon color="disabled" />}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Scrollbar>
            <TablePagination
                component="div"
                count={filteredQuestions.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={page}
                rowsPerPage={limit}
                rowsPerPageOptions={[5, 10, 25]}
            />
        </Card>
    );
};
